// "Add invoice" flow for one amount line of the request form:
//   Add invoice → bottom sheet (Take photo / Choose file) → images go through a full-screen
//   crop dialog (react-image-crop: a draggable + freely resizable crop rectangle, 90° rotation
//   baked into the bitmap), PDFs upload as-is → XHR upload with a progress bar → a chip per
//   uploaded file.
// New-request lines just collect {filePath} (the row is created by apiSaveRequest); lines that
// already exist server-side (edit mode) pass amountId so the attachment row persists immediately.

import { useCallback, useRef, useState, type SyntheticEvent } from "react";
import ReactCrop, { type Crop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";
import { Camera, FolderOpen, Paperclip, RotateCw, X } from "lucide-react";

import { ApiError } from "@/lib/api";
import { deleteAttachment, downloadAttachmentBlob, uploadAttachment } from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/** One attachment on a form line. `id` present ⇒ persisted server-side (edit mode). */
export interface AttachmentChip {
  id?: number;
  filePath: string;
  /** Object URL of the file uploaded in THIS session — lets the chip open instantly. */
  localUrl?: string;
}

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

const MAX_EDGE = 2000;

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the selected image."));
    img.src = url;
  });
}

/** Rotate the bitmap 90° clockwise and return a fresh object URL. */
async function rotateSrc90(src: string): Promise<string> {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = image.height;
  canvas.height = image.width;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available in this browser.");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92)
  );
  if (!blob) throw new Error("Could not rotate the image.");
  return URL.createObjectURL(blob);
}

/**
 * Crop from the DISPLAYED image element (the PixelCrop is in displayed pixels — scale it to
 * the natural bitmap), downscale so max edge ≤ 2000px, export as JPEG (0.85).
 */
async function cropToJpegFile(img: HTMLImageElement, crop: PixelCrop): Promise<File> {
  const scaleX = img.naturalWidth / img.width;
  const scaleY = img.naturalHeight / img.height;
  const sx = crop.x * scaleX;
  const sy = crop.y * scaleY;
  const sw = Math.max(1, Math.round(crop.width * scaleX));
  const sh = Math.max(1, Math.round(crop.height * scaleY));

  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const out = document.createElement("canvas");
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round(sh * scale));
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available in this browser.");
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, out.width, out.height);

  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/jpeg", 0.85));
  if (!blob) throw new Error("Could not export the cropped image.");
  return new File([blob], `invoice_${Date.now()}.jpg`, { type: "image/jpeg" });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InvoiceCapture({
  token,
  amountId,
  attachments,
  onChange,
  onBusyChange,
  disabled,
}: {
  token: string;
  /** Existing PaymentRequestAmount id (edit mode) — links the upload to that line immediately. */
  amountId?: number;
  attachments: AttachmentChip[];
  onChange: (next: AttachmentChip[]) => void;
  /** True while an upload/delete is in flight (the form disables Save meanwhile). */
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop dialog state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [rotating, setRotating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Upload state
  const [uploadPct, setUploadPct] = useState<number | null | undefined>(undefined); // undefined = idle
  const [removingId, setRemovingId] = useState<number | null>(null);

  const setBusy = useCallback(
    (busy: boolean) => {
      onBusyChange?.(busy);
    },
    [onBusyChange]
  );

  const closeCrop = useCallback(() => {
    setCropSrc((src) => {
      if (src) URL.revokeObjectURL(src);
      return null;
    });
    setCrop(undefined);
    setCompletedCrop(null);
    setRotating(false);
    setExporting(false);
  }, []);

  /** Default the rectangle to (almost) the whole image whenever a bitmap loads. */
  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop({ unit: "%", x: 3, y: 3, width: 94, height: 94 });
    setCompletedCrop({
      unit: "px",
      x: width * 0.03,
      y: height * 0.03,
      width: width * 0.94,
      height: height * 0.94,
    });
  }, []);

  const rotate = useCallback(() => {
    if (!cropSrc || rotating) return;
    setRotating(true);
    rotateSrc90(cropSrc)
      .then((next) => {
        URL.revokeObjectURL(cropSrc);
        // onImageLoad of the new bitmap resets the rectangle.
        setCrop(undefined);
        setCompletedCrop(null);
        setCropSrc(next);
      })
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : "Could not rotate the image.");
      })
      .finally(() => setRotating(false));
  }, [cropSrc, rotating]);

  const doUpload = useCallback(
    (file: File) => {
      setBusy(true);
      setUploadPct(0);
      uploadAttachment(token, file, amountId, (pct) => setUploadPct(pct))
        .then((r) => {
          if (r.errorMessage || !r.filePath) {
            toast.error(r.errorMessage || "Upload failed.");
            return;
          }
          onChange([
            ...attachments,
            {
              filePath: r.filePath,
              localUrl: URL.createObjectURL(file),
              ...(r.attachmentId ? { id: r.attachmentId } : {}),
            },
          ]);
          toast.success(r.myMessage || "Invoice uploaded.");
        })
        .catch((e) => {
          toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Upload failed.");
        })
        .finally(() => {
          setUploadPct(undefined);
          setBusy(false);
        });
    },
    [token, amountId, attachments, onChange, setBusy]
  );

  const onPicked = useCallback(
    (input: HTMLInputElement) => {
      const file = input.files?.[0];
      input.value = ""; // allow re-picking the same file
      if (!file) return;
      setMenuOpen(false);
      if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
        doUpload(file); // PDFs skip cropping
        return;
      }
      setCropSrc(URL.createObjectURL(file));
    },
    [doUpload]
  );

  const confirmCrop = useCallback(() => {
    const img = imgRef.current;
    if (!img || !completedCrop || completedCrop.width < 5 || completedCrop.height < 5) return;
    setExporting(true);
    cropToJpegFile(img, completedCrop)
      .then((file) => {
        closeCrop();
        doUpload(file);
      })
      .catch((e) => {
        setExporting(false);
        toast.error(e instanceof Error ? e.message : "Could not process the image.");
      });
  }, [completedCrop, closeCrop, doUpload]);

  const removeChip = useCallback(
    (chip: AttachmentChip, index: number) => {
      if (chip.id) {
        // Persisted attachment (edit mode): soft-delete server-side first.
        setRemovingId(chip.id);
        setBusy(true);
        deleteAttachment(token, chip.id)
          .then((r) => {
            onChange(attachments.filter((_, i) => i !== index));
            toast.success(r.myMessage || "Attachment removed.");
          })
          .catch((e) => {
            toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not remove the attachment.");
          })
          .finally(() => {
            setRemovingId(null);
            setBusy(false);
          });
      } else {
        // Uploaded but not saved into the request yet — just drop the chip.
        onChange(attachments.filter((_, i) => i !== index));
      }
    },
    [token, attachments, onChange, setBusy]
  );

  const uploading = uploadPct !== undefined;

  // Open a chip: session uploads open from their local object URL instantly; persisted
  // attachments (edit mode) are fetched with the auth header and opened as a blob URL.
  const [viewingKey, setViewingKey] = useState<string | null>(null);
  const openChip = useCallback(
    (chip: AttachmentChip, key: string) => {
      const openUrl = (url: string) => {
        const w = window.open(url, "_blank");
        if (!w) {
          const a = document.createElement("a");
          a.href = url;
          a.download = chip.filePath;
          a.click();
        }
      };
      if (chip.localUrl) {
        openUrl(chip.localUrl);
        return;
      }
      if (!chip.id) return;
      setViewingKey(key);
      downloadAttachmentBlob(token, chip.id)
        .then((blob) => openUrl(URL.createObjectURL(blob)))
        .catch((e) => {
          toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not open the file.");
        })
        .finally(() => setViewingKey(null));
    },
    [token]
  );

  return (
    <div className="flex flex-col gap-2">
      {attachments.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {attachments.map((a, i) => (
            <li
              key={a.id ?? `${a.filePath}-${i}`}
              className="flex max-w-full items-center gap-1 rounded-full border border-border bg-muted/50 py-0.5 pr-1 pl-2 text-xs"
            >
              <Paperclip className="size-3 shrink-0 text-muted-foreground" />
              <button
                type="button"
                className={cn(
                  "max-w-40 truncate text-left text-primary underline-offset-2 hover:underline",
                  viewingKey === (a.id ? `id-${a.id}` : `i-${i}`) && "animate-pulse"
                )}
                onClick={() => openChip(a, a.id ? `id-${a.id}` : `i-${i}`)}
              >
                {a.filePath}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="size-5 rounded-full"
                aria-label={`Remove ${a.filePath}`}
                disabled={disabled || removingId === a.id || uploading}
                onClick={() => removeChip(a, i)}
              >
                <X className={cn("size-3", removingId === a.id && "animate-pulse")} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {uploading && (
        <div className="flex items-center gap-2">
          <Progress value={uploadPct ?? null} className="flex-1" />
          <span className="w-9 text-right text-xs text-muted-foreground">
            {uploadPct == null ? "…" : `${uploadPct}%`}
          </span>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled || uploading}
        onClick={() => setMenuOpen(true)}
      >
        <Camera /> Add invoice
      </Button>

      {/* Hidden pickers (triggered from the sheet — no native controls are ever visible). */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onPicked(e.currentTarget)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onPicked(e.currentTarget)}
      />

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="pb-6">
          <SheetHeader>
            <SheetTitle>Add invoice</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-2 px-4">
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera /> Take photo
            </Button>
            <Button
              type="button"
              variant="outline"
              className="justify-start"
              onClick={() => fileInputRef.current?.click()}
            >
              <FolderOpen /> Choose file
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Full-screen crop dialog: drag the rectangle to move it, drag its edges/corners to
          resize in either direction. */}
      <Dialog open={!!cropSrc} onOpenChange={(open) => !open && closeCrop()}>
        <DialogContent className="top-0 left-0 flex h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-none bg-black p-0 text-white">
          <DialogTitle className="sr-only">Crop invoice photo</DialogTitle>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-2">
            {cropSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                ruleOfThirds
                keepSelection
                minWidth={20}
                minHeight={20}
                className={cn("max-h-full max-w-full", rotating && "opacity-50")}
              >
                <img
                  ref={imgRef}
                  src={cropSrc}
                  alt="Invoice to crop"
                  onLoad={onImageLoad}
                  className="max-h-[calc(100dvh-6.5rem)] w-auto max-w-full select-none"
                />
              </ReactCrop>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 bg-black/90 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10 hover:text-white"
              aria-label="Rotate 90 degrees"
              disabled={rotating || exporting}
              onClick={rotate}
            >
              <RotateCw className={cn(rotating && "animate-spin")} />
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white"
              onClick={closeCrop}
              disabled={exporting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={confirmCrop} disabled={exporting || rotating || !completedCrop}>
              {exporting ? "Processing…" : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
